import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { StateBadge, STATE_KEY } from "@/components/state-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/field";
import { ErrorState } from "@/components/query-state";
import { formatDateTime, formatRelativeDateTime } from "@/lib/format";
import type { PolicyState } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { useHolyModeOptional } from "@/preferences/holy-mode";
import { SacredIcon } from "@/components/sacred-icon";

export function PoliciesList() {
  const { t, i18n } = useTranslation();
  const { enabled } = useHolyModeOptional();
  const [params, setParams] = useSearchParams();
  const domain = params.get("domain") ?? "";
  const state = params.get("state") ?? "";
  const search = params.get("q") ?? "";

  const domains = useQuery({ queryKey: ["domains"], queryFn: api.listDomains });
  // One request for every domain: a request per domain change costs a CORS preflight plus a GET.
  const policies = useQuery({ queryKey: ["policies", "all"], queryFn: () => api.listPolicies() });

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const matchingSearch = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return (policies.data ?? []).filter((policy) =>
      (!domain || policy.domainKey === domain)
      && `${policy.title ?? ""} ${policy.policyId}`.toLocaleLowerCase().includes(needle),
    );
  }, [policies.data, domain, search]);
  const visiblePolicies = matchingSearch.filter((policy) => !state || policy.state === state);

  const loading = <div role="status" aria-label={t("policies.loading")} className="border-border bg-surface rounded-xl border p-5">
    <div className="mb-3 grid grid-cols-5 gap-4 text-xs"><span className="h-4 animate-pulse rounded bg-surface-sunken" /><span /><span /><span /><span /></div>
    {Array.from({ length: 5 }, (_, index) => <div key={index} className="border-border-subtle grid grid-cols-5 gap-4 border-t py-4">
      <span className="h-4 animate-pulse rounded bg-surface-sunken" /><span className="h-4 animate-pulse rounded bg-surface-sunken" /><span className="h-4 animate-pulse rounded bg-surface-sunken" /><span className="h-4 animate-pulse rounded bg-surface-sunken" /><span className="h-4 animate-pulse rounded bg-surface-sunken" />
    </div>)}
  </div>;

  return (
    <>
      <PageHeader
        title={t("policies.title")}
        description={t("policies.description")}
        sacredIcon="tablets"
        sacredCaption="holy.policiesCaption"
        actions={<Link to="/policies/new"><Button><Plus size={16} aria-hidden /> {t("policies.create")}</Button></Link>}
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Field label={t("policies.searchLabel")} htmlFor="filter-search">
            <Input id="filter-search" type="search" role="searchbox" value={search} placeholder={t("policies.searchPlaceholder")} onChange={(e) => setFilter("q", e.target.value)} />
          </Field>
        </div>
        <div className="w-48">
          <Field label={t("policies.domainLabel")} htmlFor="filter-domain">
            <Select id="filter-domain" value={domain} onChange={(e) => setFilter("domain", e.target.value)}>
              <option value="">{t("common.all")}</option>
              {(domains.data ?? []).map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
            </Select>
          </Field>
        </div>
        <div className="w-48">
          <Field label={t("policies.stateLabel")} htmlFor="filter-state">
            <Select id="filter-state" value={state} onChange={(e) => setFilter("state", e.target.value)}>
              <option value="">{t("policies.countAll", { count: matchingSearch.length })}</option>
              {(Object.keys(STATE_KEY) as PolicyState[]).map((s) => (
                <option key={s} value={s}>{t(STATE_KEY[s])} ({matchingSearch.filter((policy) => policy.state === s).length})</option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {policies.isPending ? loading : policies.isError ? <ErrorState error={policies.error} /> : (
        <Card className="overflow-hidden">
          {visiblePolicies.length === 0 ? (
            enabled ? <div className="holy-empty-state"><SacredIcon name="tablets" size={34} /><p>{t("policies.empty")}</p></div>
              : <p className="text-muted p-8 text-center text-sm">{t("policies.empty")}</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-sunken text-muted text-xs">
                <tr>
                  <th className="px-5 py-2.5 font-medium">{t("policies.tablePolicy")}</th>
                  <th className="px-5 py-2.5 font-medium">{t("policies.tableDomain")}</th>
                  <th className="px-5 py-2.5 font-medium">{t("policies.tableState")}</th>
                  <th className="px-5 py-2.5 font-medium">{t("policies.tableVersion")}</th>
                  <th className="px-5 py-2.5 font-medium">{t("policies.tableUpdated")}</th>
                </tr>
              </thead>
              <tbody>
                {visiblePolicies.map((p) => (
                  <tr key={p.policyId} className="border-border-subtle hover:bg-surface-elevated border-t">
                    <td className="px-5 py-3">
                      <Link to={`/policies/${p.policyId}`} className="text-foreground font-medium hover:underline">{p.title ?? p.policyId}</Link>
                      <div className="text-muted text-xs">{p.policyId}</div>
                    </td>
                    <td className="text-secondary px-5 py-3">{p.domainKey}</td>
                    <td className="px-5 py-3"><StateBadge state={p.state} /></td>
                    <td className="text-secondary px-5 py-3">{p.liveVersion === null ? "—" : `v${p.liveVersion}`}</td>
                    <td className="text-secondary px-5 py-3">{p.updatedAt ? <time dateTime={p.updatedAt} title={formatDateTime(p.updatedAt, i18n.resolvedLanguage)}>{formatRelativeDateTime(p.updatedAt, i18n.resolvedLanguage)}</time> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}
